<template>
  <div class="uk-container">
    <div class="uk-flex-center" uk-grid>
      <div class="uk-width-large">
        <div v-if="dentist.request" class="uk-alert-warning" uk-alert>
          <a class="uk-alert-close" uk-close />
          <div class="uk-text-center">
            <p>Your last request is being processed. Please allow couple of days for the request to be resolved.</p>
            <p class="uk-margin-remove-bottom">You can contact the Administrator for additional details.</p>
          </div>
        </div>

        <div class="uk-card uk-card-default">
          <div class="uk-card-header uk-text-center">
            <h3 class="uk-card-title">Request Update</h3>
            <p class="uk-text-meta">Request an update of details about your practice. <span uk-tooltip="These details (Practice Name and Location) can only be changed by administrator and therefore you need to submit a request and it might take a while before they are updated."><i class="icon gg-info" /></span></p>
          </div>
          <div class="uk-card-body">
            <ValidationObserver ref="observer-request-info" tag="div" class="uk-form-stacked">
              <div class="uk-margin">
                <ValidationProvider v-slot="{ errors }" rules="required">
                  <label class="uk-form-label uk-text-bold" for="name" :class="{ 'uk-text-danger': errors.length }">Practice Name</label>
                  <div class="uk-form-controls">
                    <input id="name" v-model="name" class="uk-input" :class="{ 'uk-form-danger': errors.length }" type="text" name="name" :disabled="dentist.request">
                  </div>
                  <div v-if="errors.length" class="form-error">{{ errors[0] }}</div>
                </ValidationProvider>
              </div>

              <div class="uk-margin">
                <ValidationProvider v-slot="{ errors }" rules="required|email">
                  <label class="uk-form-label uk-text-bold" for="email" :class="{ 'uk-text-danger': errors.length }">Notification Email</label>
                  <div class="uk-form-controls">
                    <input id="email" v-model="email" class="uk-input" :class="{ 'uk-form-danger': errors.length }" type="text" name="email" :disabled="dentist.request">
                  </div>
                  <div v-if="errors.length" class="form-error">{{ errors[0] }}</div>
                </ValidationProvider>
              </div>

              <div class="uk-margin">
                <ValidationProvider v-slot="{ errors }" rules="required">
                  <label class="uk-form-label uk-text-bold" for="phone-primary" :class="{ 'uk-text-danger': errors.length }">Phone Number - Practice</label>
                  <div class="uk-form-controls">
                    <input id="phone-primary" v-model="phone.primary" class="uk-input" :class="{ 'uk-form-danger': errors.length }" type="text" name="phone-primary" :disabled="dentist.request">
                  </div>
                  <div v-if="errors.length" class="form-error">{{ errors[0] }}</div>
                </ValidationProvider>
              </div>

              <div class="uk-margin">
                <ValidationProvider v-slot="{ errors }" rules="required">
                  <label class="uk-form-label uk-text-bold" for="phone-secondary" :class="{ 'uk-text-danger': errors.length }">Phone Number - On-Call & Notifications</label>
                  <div class="uk-text-meta">This number should be a mobile number capable of receiving Text Messages (SMS).</div>
                  <div class="uk-form-controls">
                    <input id="phone-secondary" v-model="phone.secondary" class="uk-input" :class="{ 'uk-form-danger': errors.length }" type="text" name="phone-secondary" :disabled="dentist.request">
                  </div>
                  <div v-if="errors.length" class="form-error">{{ errors[0] }}</div>
                </ValidationProvider>
              </div>
            </ValidationObserver>

            <ValidationObserver ref="observer-request-location" v-slot="{ invalid, touched }" tag="div" class="uk-form-stacked">
              <div class="uk-margin-small">
                <div class="uk-form-label uk-text-bold" :class="{ 'uk-text-danger': (invalid && touched) }">Location</div>
                <ValidationProvider v-slot="{ errors }" rules="required">
                  <div class="uk-form-controls">
                    <input v-model="location.house" class="uk-input" :class="{ 'uk-form-danger': errors.length }" type="text" name="house" placeholder="House Number/Name" :disabled="dentist.request">
                  </div>
                  <div v-if="errors.length" class="form-error">{{ errors[0] }}</div>
                </ValidationProvider>
              </div>

              <div class="uk-margin-small">
                <ValidationProvider v-slot="{ errors }" rules="required">
                  <div class="uk-form-controls">
                    <input v-model="location.street" class="uk-input" :class="{ 'uk-form-danger': errors.length }" type="text" name="street" placeholder="Street Name" :disabled="dentist.request">
                  </div>
                  <div v-if="errors.length" class="form-error">{{ errors[0] }}</div>
                </ValidationProvider>
              </div>

              <div class="uk-margin-small">
                <ValidationProvider v-slot="{ errors }" rules="required">
                  <div class="uk-form-controls">
                    <input v-model="location.city" class="uk-input" :class="{ 'uk-form-danger': errors.length }" type="text" name="city" placeholder="City/Town" :disabled="dentist.request">
                  </div>
                  <div v-if="errors.length" class="form-error">{{ errors[0] }}</div>
                </ValidationProvider>
              </div>

              <div class="uk-margin-small">
                <ValidationProvider v-slot="{ errors }" rules="required">
                  <div class="uk-form-controls">
                    <input v-model="location.postcode" class="uk-input" :class="{ 'uk-form-danger': errors.length }" type="text" name="postcode" placeholder="Postcode" :disabled="dentist.request">
                  </div>
                  <div v-if="errors.length" class="form-error">{{ errors[0] }}</div>
                </ValidationProvider>
              </div>
            </ValidationObserver>

            <div class="uk-margin">
              <div class="uk-form-label uk-text-bold">Features</div>
              <template v-for="feature in $helpers.practiceFeatures">
                <div :key="feature.id" class="uk-margin-small">
                  <div>
                    {{ feature.value }}
                    <switch-button :labels="{ false: 'No', true: 'Yes' }" :status.sync="features[feature.id].status" style="display: inline-flex; margin: 5px 0 5px 20px;" :disabled="dentist.request" />
                  </div>
                  <textarea v-model="features[feature.id].notes" class="uk-textarea" cols="30" rows="5" :disabled="dentist.request" />
                </div>
              </template>
            </div>

            <div class="uk-margin">
              <div class="uk-form-label uk-text-bold">Notifications Preferences</div>
              <div class="uk-form-controls uk-margin-small-top">
                <label class="uk-margin-small-right uk-margin-small-left" for="email-checkbox"><input id="email-checkbox" v-model="notifications.email" class="uk-checkbox" type="checkbox"> Email</label>
                <label class="uk-margin-small-right uk-margin-small-left" for="sms-checkbox"><input id="sms-checkbox" v-model="notifications.phone" class="uk-checkbox" type="checkbox"> SMS</label>
              </div>
            </div>
          </div>

          <div class="uk-card-footer uk-text-center">
            <button class="uk-button uk-button-primary" :disabled="dentist.request" @click="request">Submit Request</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import SwitchButton from '@/components/SwitchButton.vue'

export default {
  name: 'ViewDentistPracticeUpdate',

  components: {
    ValidationProvider,
    ValidationObserver,
    SwitchButton
  },

  data() {
    return {
      name: null,
      phone: null,
      email: null,
      location: {
        house: null,
        street: null,
        city: null,
        postcode: null
      },
      features: null,
      notifications: null
    }
  },

  computed: {
    dentist() {
      return this.$store.getters['dentists/find'](this.$route.params.id)
    }
  },

  created() {
    this.name = this.dentist.name
    this.phone = this.dentist.phone
    this.email = this.dentist.email
    this.location = this.dentist.location
    this.features = { ...this.$helpers.practiceFeatures, ...this.dentist.features }
    this.notifications = this.dentist.notifications
  },

  methods: {
    async request() {
      const isValidInfo = await this.$refs['observer-request-info'].validate()
      const isValidLocation = await this.$refs['observer-request-location'].validate()

      if (isValidInfo && isValidLocation) {
        const request = {
          info: {
            type: 'dentist',
            status: 'new',
            created: {
              timestamp: this.$firebase.firestore.FieldValue.serverTimestamp(),
              user: this.$store.state.account.record.uid
            }
          },
          content: {
            dentist: this.dentist.id,
            form: {
              name: this.name,
              phone: this.phone,
              email: this.email,
              location: this.location,
              features: this.features,
              notifications: this.notifications
            }
          },
          log: [{
            timestamp: this.$firebase.firestore.Timestamp.now(),
            user: this.$store.state.account.record.uid,
            type: 'create'
          }]
        }

        try {
          await this.$firebase.firestore().collection(this.$globals.firebase.collection.dentists).doc(this.dentist.id).update({ request: true })
          await this.$firebase.firestore().collection(this.$globals.firebase.collection.requests).add(request)
          this.$uikit.notification('Your request was submitted successfully!', 'success')
        } catch (error) {
          console.log(error)
          this.$uikit.notification('Something went wrong while submitting your request. Please try again later, or use different support method.', 'danger')
        }
      }
    }
  }
}
</script>
